import { useEffect } from "react";
import { useObservable } from "@rsm";
import { getClientService } from "services/client.service";
import { getUserService } from "services/user.service";
import { useSessionMessage } from "hooks/useSessionMessage";
import { useIsChurned } from "hooks/useIsChurned";
import { toast, removeToast, type CustomToastMessage } from "components/ToastProvider/ToastProvider";

const INACTIVE_TOAST: CustomToastMessage = {
  severity: "error",
  sticky: true,
  closable: false,
  centered: true,
  summary:
    "This account is inactive. Please get in touch with your representative to continue using Ideally.",
};

/**
 * Shows the "account is inactive" banner and, for Ideally admins, a dismissable note explaining the
 * customer lockdown. Call from any surface a restricted user can reach (allowed pages via
 * LoggedInModuleHOC, and the ChurnBlockedView landing page) so the banner fires even when the route
 * is blocked.
 *
 * The inactive banner ALWAYS shows and is never closable (for users and admins alike), so it is a
 * plain sticky toast rather than a once-per-session message. A single shared message reference is
 * shown while inactive and removed when the state clears, so remounts across navigation don't stack
 * duplicates. The admin note stays a session message (closable, once per session).
 *
 * The admin note is keyed on the user id, not the account, so dismissing it once silences it for the
 * rest of the session regardless of switching client / org / team.
 */
export const useChurnBanner = (): void => {
  const client = useObservable(getClientService().client$);
  const user = useObservable(getUserService().user$);
  const { isRestricted, isIdeallyAdmin } = useIsChurned();
  // Override suppresses everything: no banner, no admin note (isRestricted already excludes overridden accounts).
  const showInactive = !!client && isRestricted;

  useEffect(() => {
    if (!showInactive) return;
    toast(INACTIVE_TOAST);
    return () => removeToast(INACTIVE_TOAST);
  }, [showInactive]);

  useSessionMessage({
    shouldShow: showInactive && isIdeallyAdmin,
    message: {
      summary: "Ideally admins only",
      detail:
        "This customer's account is inactive. Members can view transaction history only, they cannot open past tests, create studies or use Canvas. You are not restricted. The customer does not see this message.",
    },
    delayMs: 0,
    sessionStorageKey: `${user?.id ?? 0}_account_inactive_admin`,
    severity: "info",
  });
};
